import React, { useState } from 'react';
import { Box, Button, Checkbox, IconButton, Input, InputAdornment, Menu, MenuItem, Modal, Paper, styled, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Typography } from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SaveIcon from '@mui/icons-material/Save';
import HtmlTooltip from '../Tooltip/HtmlTooltip';
import TooltipContent from '../Tooltip/TooltipContent';
import CustomLinearProgress from '../Loaders/CustomLinearProgress';
import { addToSentItemsLS, fetchWrapper, fetchWrapperNoAuth, SERVER } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import ErrorModal from '../Modals/ErrorModal';
import ConfirmModal from '../Modals/ConfirmModal';
import CustomInput from '../Inputs/CustomInput';
import BarcodeLabelModal from '../Modals/BarcodeLabelModal';
import UpdatePriceModal from '../Modals/UpdatePriceModal';
import UpdateQtyModal from '../Modals/UpdateQtyModal';

const ProductsTable = ({ products, isLoading, markedProducts, setMarkedProducts, handleChangePage, handleChangeRowsPerPage, rowsPerPage, page, paginatedProducts }) => {
  const { theme } = useTheme();
  const { encodedAuth, user, password, isSubUser } = useAuth();

  const [open, setOpen] = useState(false);
  const [barcodeLabelProduct, setBarcodeLabelProduct] = useState({});
  const [openBarcodeModal, setOpenBarcodeModal] = useState(false);
  const [openErrModal, setOpenErrModal] = useState(false);
  const [openUpdateBatchNameModal, setOpenUpdateBatchNameModal] = useState(false);
  const [openUpdatePrice, setOpenUpdatePrice] = useState(false);
  const [openUpdateQty, setOpenUpdateQty] = useState(false);
  const [tempProduct, setTempProduct] = useState({});
  const [currProduct, setCurrProduct] = useState({});
  const [updatedBatchName, setUpdatedBatchName] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [deleteSku, setDeleteSku] = useState("");

  const pageSizeOptions = [5, 10, 25];

  const [anchorEls, setAnchorEls] = useState({});

  const [checkAll, setCheckAll] = useState(false);
  const [renderer, setRenderer] = useState(0);

  const getChipColor = (status) => {
    if (status === "PUBLISHED" || status === "ACTIVE") {
      return theme.accent.success;
    } else if (status === "ERROR" || status === "RETIRED" || status === "SYSTEM_PROBLEM") {
      return theme.accent.error;
    } else {
      return theme.accent.warning;
    }
  };

  const handleCheckboxChange = (product) => {
    if (product.publishedStatus !== "PUBLISHED") {
      setErrMsg("The product must be 'PUBLISHED' before it can be shipped.");
      setOpenErrModal(true);
      return;
    }
    if (markedProducts.includes(product.sku)) {
      setMarkedProducts((prevArray) => prevArray.filter(sku => sku !== product.sku));
    } else {
      setMarkedProducts((prevArray) => [...prevArray, product.sku]);
    }
    setCheckAll(false);
  };

  const handleCheckboxChangeAll = () => {
    if (checkAll) {
      setMarkedProducts([]);
    } else {
      setMarkedProducts(products.map(item => item.sku));
    }
    setCheckAll(!checkAll);
  };

  const retireItem = async (sku) => {
    const url = `${SERVER}/delete_retire_an_item/${sku}`;
    try {
      const response = await fetchWrapper(url, user, password, encodedAuth);
      setOpen(false);
      setErrMsg(response.message);
      setOpenErrModal(true);
    } catch (error) {
      setOpen(false);
      setErrMsg(error.toString());
      setOpenErrModal(true);
    }
  };

  const handleClick = (event, sku) => {
    setAnchorEls(prev => ({
      ...prev,
      [sku]: event.currentTarget
    }));
  };

  const handleClose = (sku) => {
    setAnchorEls(prev => ({
      ...prev,
      [sku]: null
    }));
  };

  const handleChangeBatch = async (product, batchName) => {
    const metadata = {
      batchName: batchName === "" ? "none" : batchName,
      email: user,
      productName: product.productName,
      image: product.image,
      sku: product.sku,
      gtin: product.gtin,
      upc: product.upc,
      cost: product.cost,
      qty: product.qty,
      innerPack: product.innerPack,
      source: product.source,
      note: product.note,
      expirationDate: product.expiration_date || null,
      inboundOrderId: product.inbound_order_id,
      shipmentId: product.shipment_id,
      purchaseDate: product.purchase_date || "",
      productType: product.product_type,
      schema: product.schema,
      MPItem: product.mp_item
    }
    const body = {
      sku: product.sku,
      email: user,
      batchName: batchName === "" ? "none" : batchName,
      metadata: metadata
    }
    try {
      await fetchWrapperNoAuth(`${SERVER}/falcon_update_batch`, user, password, { method: 'POST', body: body });
    } catch (error) {
      console.error('Error update batch:', error.message);
    }
    setOpenUpdateBatchNameModal(false);
    window.location.reload();
  };

  const styles = {
    container: {
      maxHeight: isSubUser ? 'calc(70vh - 40px)' : '70vh', // Adjust the height as needed
      overflowY: 'auto', // Enable vertical scrolling
      overflowX: 'auto', // Enable vertical scrolling
      backgroundColor: theme.background.secondary,
      '&::-webkit-scrollbar': {
        width: '10px', // Width of the scrollbar
        height: '10px', // Width of the scrollbar
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: theme.background.primary, // Track color
        borderRadius: '3px', // Optional: rounded corners
        border: `1px solid ${theme.border.light}`,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.scrollbar, // Thumb color
        borderRadius: '15px', // Optional: rounded corners
        cursor: 'grab',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: theme.scrollbarHover, // Thumb color on hover
      },
      '&::-webkit-scrollbar-button': {
        display: 'none', // Thumb color on hover
      },
    },
    th: {
      fontWeight: "bold",
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: 16,
      position: "sticky", // Keeps the header in place
      top: 0, // Position at the top
      backgroundColor: theme.background.secondary, // Same background color for header
      zIndex: 1, // Ensures the header is above the scrollable body
      py: '15px',
    },
    td: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      fontSize: 14,
      py: '10px'
    },
    chip: {
      fontFamily: theme.fontFamily,
      color: '#2C2C2C',
      borderRadius: '20px',
      width: 'fit-content',
      px: '10px',
      fontSize: '14px'
    },
    modal: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '26vw',
      bgcolor: theme.background.primary,
      borderRadius: 2,
      border: `4px solid ${theme.color.primary}`,
      boxShadow: 24,
      p: 4,
      outline: 'none',
    },
    button: {
      textTransform: 'none',
      fontFamily: theme.fontFamily,
      color: theme.text.negative,
      borderRadius: '20px',
      border: `1px solid ${theme.border.light}`,
      backgroundColor: theme.accent.success,
    },
    selectProps: {
      sx: {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
        backgroundColor: theme.background.primary,
        '& .MuiSelect-icon': {
          color: theme.text.primary,
        },
      },
      MenuProps: {
        sx: {
          '& .MuiPaper-root': {
            backgroundColor: theme.background.primary,
          },
          '& .MuiMenuItem-root': {
            fontFamily: theme.fontFamily,
            color: theme.text.primary,
          },
        },
      },
    },
    pagination: {
      position: 'sticky',
      bottom: -5,
      backgroundColor: theme.background.secondary,
      zIndex: 2,
      '& .MuiTablePagination-root': {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
      },
      '& .MuiTablePagination-selectIcon': {
        color: theme.text.primary,
      },
      '& .MuiInputBase-root': {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
        backgroundColor: theme.background.secondary,
      },
      '& .MuiTablePagination-select': {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
      },
      '& .MuiTablePagination-selectLabel': {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
      },
      '& .MuiSelect-select': {
        backgroundColor: theme.background.secondary,
      },
      '& .MuiMenu-paper': {
        backgroundColor: theme.background.primary,
      },
      '& .MuiTablePagination-menuItem': {
        fontFamily: theme.fontFamily,
        color: theme.text.primary,
      },
      '& .MuiTablePagination-actions': {
        '& .MuiIconButton-root': {
          color: theme.text.primary,
          '&.Mui-disabled': {
            color: theme.accent.disabled,
          }
        }
      }
    },
    underlineDotted: {
      textDecoration: 'underline dotted',
      textUnderlineOffset: '4px',
      cursor: 'pointer',
    },
  };

  return (
    <Box m={2}>
      <TableContainer
        component={Paper}
        sx={styles.container}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.th}>
                <Checkbox
                  checked={checkAll}
                  onChange={handleCheckboxChangeAll}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fill: theme.color.primary, // Icon color inside the checkbox
                    },
                  }}
                />
              </TableCell>
              <TableCell sx={{ ...styles.th, width: '15vw', }}>Item</TableCell>
              <TableCell sx={{ ...styles.th, width: '8vw', }}>SKU</TableCell>
              <TableCell sx={{ ...styles.th, width: '6vw', }}>Batch</TableCell>
              <TableCell sx={{ ...styles.th, width: '5vw', }}>Price (USD)</TableCell>
              <TableCell sx={{ ...styles.th, width: '5vw', }}>Cost (USD)</TableCell>
              <TableCell sx={styles.th}>Quantity</TableCell>
              <TableCell sx={styles.th}>Status</TableCell>
              <TableCell sx={styles.th}>Last Modified</TableCell>
              <TableCell sx={styles.th}>Lifecycle Status</TableCell>
              <TableCell sx={styles.th}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts.map((product, index) => (
              <TableRow key={index} render={renderer} sx={{ backgroundColor: markedProducts.includes(product.sku) && `${theme.color.secondary}25` }}>
                <TableCell>
                  <Checkbox
                    checked={markedProducts.includes(product.sku)}
                    onChange={() => handleCheckboxChange(product)}
                    sx={{
                      '& .MuiSvgIcon-root': {
                        fill: theme.color.primary, // Icon color inside the checkbox
                      },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }} onClick={() => window.open(`https://www.walmart.com/ip/${product.wpid}`)}>
                    <img
                      id={`hover-img-${product.wpid}`}
                      loading="lazy"
                      src={product.image || "https://i5.walmartimages.com/seo/Another-Product-Image.jpeg"}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null; // Prevents looping
                        currentTarget.src = "https://i5.walmartimages.com/seo/Another-Product-Image.jpeg";
                      }}
                      style={{
                        borderRadius: "5px",
                        border: `1.5px solid ${theme.color.primary}`,
                        height: "1.5vw",
                        width: "1.5vw",
                        objectFit: "scale-down",
                        position: "relative",
                        marginRight: '10px'
                      }}
                      onMouseEnter={() => {
                        const img = document.getElementById(`hover-img-${product.wpid}`);
                        img.style.transform = "scale(1.5)";
                        img.style.boxShadow = "1px 1px 15px 1px gray";
                        img.style.backgroundColor = "white";
                        img.style.cursor = "pointer";
                      }}
                      onMouseLeave={() => {
                        const img = document.getElementById(`hover-img-${product.wpid}`);
                        img.style.transform = "none";
                        img.style.boxShadow = "none";
                        img.style.backgroundColor = "none";
                        img.style.cursor = "none";
                      }}
                    />
                    <HtmlTooltip
                      customtheme={theme}
                      title={
                        <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                          {product.productName}
                        </Typography>
                      }
                      arrow
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -15],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <Typography sx={{ ...styles.td, width: '15vw', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', }}>
                        {product.productName}
                      </Typography>
                    </HtmlTooltip>
                  </Box>
                </TableCell>
                <TableCell>
                  <HtmlTooltip
                    customtheme={theme}
                    title={
                      <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                        {product.sku}
                      </Typography>
                    }
                    arrow
                    placement="top"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -15],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <Typography sx={{ ...styles.td, width: '8vw', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: "pointer" }}>
                      {product.sku}
                    </Typography>
                  </HtmlTooltip>
                </TableCell>
                <TableCell>
                  {(product.batch_name && product.batch_name !== "none") ? (
                    <HtmlTooltip
                      customtheme={theme}
                      title={
                        <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                          {product.batch_name}
                        </Typography>
                      }
                      arrow
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -15],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <Typography sx={{ ...styles.td, width: '6vw', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: "pointer" }}>
                        {product.batch_name}
                      </Typography>
                    </HtmlTooltip>
                  ) : (
                    <>-</>
                  )}
                </TableCell>
                <TableCell>
                  <Typography sx={{ ...styles.td, width: '5vw', ...styles.underlineDotted }} onClick={() => { setTempProduct(product); setOpenUpdatePrice(true); }}>
                    $ {product.price}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ ...styles.td, width: '5vw', }}>
                    $ {product.cost}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ ...styles.td, width: '5vw', ...styles.underlineDotted }} onClick={() => { setTempProduct(product); setOpenUpdateQty(true); }}>
                    {product.qty || '0'}
                  </Typography>
                  {/* <Input
                    type="number"
                    placeholder={product.qty || 0}
                    onChange={(e) => { product.qty = e.target.value; setRenderer(p => p + 1) }}
                    sx={{
                      ...styles.td,
                      width: '4vw',
                      py: 0,
                      '&.MuiInput-underline:before': {
                        borderBottomColor: theme.text.primary
                      },
                      '&.MuiInput-underline:after': {
                        borderBottomColor: theme.text.primary
                      }
                    }}
                    startAdornment={<InputAdornment position="start"><Typography sx={{ ...styles.td, py: 0 }}>#</Typography></InputAdornment>}
                    inputProps={{
                      min: 0,
                      step: 1,
                    }}
                  /> */}
                </TableCell>
                <TableCell sx={styles.td}>
                  {product.publishedStatus !== "PUBLISHED" ? (
                    <HtmlTooltip
                      customtheme={theme}
                      title={
                        <TooltipContent
                          reasons={product.unpublishedReasons?.reason}
                          theme={theme}
                        />
                      }
                      arrow
                      placement="left"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -5],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <Typography sx={{ ...styles.chip, backgroundColor: getChipColor(product.publishedStatus), cursor: 'pointer', }}>
                        {product.publishedStatus}
                      </Typography>
                    </HtmlTooltip>
                  ) : (
                    <Typography sx={{ ...styles.chip, backgroundColor: getChipColor(product.publishedStatus), }}>
                      {product.publishedStatus}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography sx={{ ...styles.td, width: '10vw' }}>
                    {new Date(product.last_modified).toUTCString().split(' GMT')[0] || "None"}
                  </Typography>
                </TableCell>
                <TableCell sx={styles.td}>
                  <Typography sx={{ ...styles.chip, backgroundColor: getChipColor(product.lifecycleStatus), }}>
                    {product.lifecycleStatus}
                  </Typography>
                </TableCell>
                <TableCell sx={{ ...styles.td, textAlign: 'center' }}>
                  <HtmlTooltip
                    customtheme={theme}
                    title={
                      <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, fontSize: 12 }}>
                        Actions
                      </Typography>
                    }
                    arrow
                    placement="top"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -5],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <IconButton
                      id={`actions-${product.sku}-btn`}
                      aria-controls={anchorEls[product.sku] ? `actions-${product.sku}` : undefined}
                      aria-expanded={anchorEls[product.sku] ? 'true' : undefined}
                      aria-haspopup="true"
                      onClick={(event) => handleClick(event, product.sku)}
                    >
                      <MoreVertIcon sx={{ cursor: 'pointer', color: theme.text.primary }} />
                    </IconButton>
                    <Menu
                      id={`actions-${product.sku}`}
                      MenuListProps={{
                        'aria-labelledby': `actions-${product.sku}-btn`,
                      }}
                      anchorEl={anchorEls[product.sku]}
                      open={Boolean(anchorEls[product.sku])}
                      onClose={() => handleClose(product.sku)}
                      PaperProps={{
                        sx: {
                          backgroundColor: theme.background.primary,
                          color: theme.text.primary,
                        },
                      }}
                    >
                      <MenuItem key={`genBarcode-${product.sku}`} onClick={() => { setBarcodeLabelProduct(product); setOpenBarcodeModal(true); handleClose(product.sku); }}>
                        <Typography sx={{ fontFamily: theme.fontFamily, }}>Generate Barcode label</Typography>
                      </MenuItem>
                      <MenuItem key={`changeBatch-${product.sku}`} onClick={() => { setUpdatedBatchName(product.batch_name); setCurrProduct(product); setOpenUpdateBatchNameModal(true); handleClose(product.sku); }}>
                        <Typography sx={{ fontFamily: theme.fontFamily, }}>Change Batch</Typography>
                      </MenuItem>
                      {isSubUser && (
                        <MenuItem key={`addToSuperUser-${product.sku}`} onClick={() => { addToSentItemsLS(user, product); handleClose(product.sku); }}>
                          <Typography sx={{ fontFamily: theme.fontFamily, }}>Add To Super User list</Typography>
                        </MenuItem>
                      )}
                      {product.lifecycleStatus !== "RETIRED" && (
                        <MenuItem key={`retireItem-${product.sku}`} onClick={() => { setDeleteSku(product.sku); handleClose(product.sku); setOpen(true); }}>
                          <Typography sx={{ fontFamily: theme.fontFamily, }}>Retire Item</Typography>
                        </MenuItem>
                      )}
                    </Menu>
                  </HtmlTooltip>
                </TableCell>
              </TableRow>
            ))}

            {isLoading && (
              <TableRow>
                <TableCell colSpan={11}>
                  <CustomLinearProgress />
                </TableCell>
              </TableRow>
            )}

            {(!isLoading && products.length === 0) && (
              <TableRow>
                <TableCell colSpan={11}>
                  <Box sx={{ width: '100%', textAlign: 'center', }}>
                    <Typography sx={styles.td}>No Data Found!</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                // colSpan={11}
                rowsPerPageOptions={pageSizeOptions}
                count={products.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ page }) => <Typography sx={{ fontFamily: theme.fontFamily, color: theme.text.primary, }}>Page {page + 1}</Typography>}
                SelectProps={styles.selectProps}
                sx={styles.pagination}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <Modal open={openUpdateBatchNameModal} onClose={() => setOpenUpdateBatchNameModal(false)}>
        <Box sx={styles.modal}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontFamily: theme.fontFamily, textAlign: 'center', color: theme.text.primary }}>
            Change Batch for <br />{currProduct.sku} item.
          </Typography>

          <CustomInput
            label={"Batch Name"}
            value={updatedBatchName}
            onChange={(e) => setUpdatedBatchName(e.target.value)}
            fullWidth
          />

          {/* Cancel button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3 }}>
            <Button variant="contained" sx={styles.button} onClick={() => handleChangeBatch(currProduct, updatedBatchName)} endIcon={<SaveIcon />}>
              Save
            </Button>
          </Box>
          <Typography sx={{ fontFamily: theme.fontFamily, textAlign: 'center', color: theme.text.primary, fontSize: 12 }}>
            when click save page will reload
          </Typography>
        </Box>
      </Modal>

      <UpdatePriceModal
        open={openUpdatePrice}
        setOpen={setOpenUpdatePrice}
        product={tempProduct}
        setProduct={setTempProduct}
      />

      <UpdateQtyModal
        open={openUpdateQty}
        setOpen={setOpenUpdateQty}
        product={tempProduct}
        setProduct={setTempProduct}
      />

      <BarcodeLabelModal open={openBarcodeModal} setOpen={setOpenBarcodeModal} product={barcodeLabelProduct} />

      <ConfirmModal open={open} setOpen={setOpen} message={`Are you sure you want to retire item ${deleteSku} ?`} confirm={() => retireItem(deleteSku)} />

      <ErrorModal open={openErrModal} setOpen={setOpenErrModal} message={errMsg} />
    </Box >
  );
};

export default ProductsTable;